/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback, useState, type Dispatch, type SetStateAction } from 'react';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import type { GenericPickerOption } from './BoardCreateFormValues.types.tsx';
import { fireBoardCreateErrorAnalytics } from './fireBoardCreateErrorAnalytics.tsx';
import { useSavedFiltersFetch } from './useSavedFiltersFetch.tsx';
import { useBoardCreateFlag } from './useBoardCreateFlag.tsx';

const loadSavedFiltersErrorTitle = defineMessage({
	id: 'software-board-create.common.load-saved-filters-error-title',
	defaultMessage: "We can't load saved filters right now",
	description:
		'Error message instructing user that the saved filters list did not load successfully',
});

const loadSavedFiltersErrorDescription = defineMessage({
	id: 'software-board-create.common.load-saved-filters-error-description',
	defaultMessage: 'Refresh the page and try again.',
	description:
		'Error description instructing user to refresh the page to try again after an error occurred.',
});

export const useIsLoading = (): [boolean, Dispatch<SetStateAction<boolean>>] => useState(false);

export const useLoadSavedFiltersController = (): {
	loadOptions: (searchString: string) => Promise<GenericPickerOption[] | undefined>;
	isLoading: boolean;
} => {
	const savedFiltersFetch = useSavedFiltersFetch();
	const [isLoading, setIsLoading] = useIsLoading();
	const { showErrorFlag } = useBoardCreateFlag();
	const { formatMessage } = useIntl();

	const loadOptions = useCallback(
		async (searchString: string) => {
			setIsLoading(true);
			return savedFiltersFetch(searchString)
				.catch((error) => {
					showErrorFlag({
						title: formatMessage(loadSavedFiltersErrorTitle),
						description: formatMessage(loadSavedFiltersErrorDescription),
					});
					fireBoardCreateErrorAnalytics({
						error: new Error(`Failed to fetch saved filters: ${error}`),
						id: 'fetchSavedFilters',
					});
					return [];
				})
				.finally(() => {
					setIsLoading(false);
				});
		},
		[formatMessage, savedFiltersFetch, setIsLoading, showErrorFlag],
	);

	return {
		loadOptions,
		isLoading,
	};
};
